import React, { useMemo } from 'react';
import { Row, Col, Button, Form } from 'react-bootstrap';
import { Search, X } from 'lucide-react';
import { PublicationStatus, StockStatus, SkinSeoulProductType, CpProductStatus } from '../../../../models/ProductTypes';
import type { BrandTerm } from '../../../../infrastructure/SkinSeoulProductsClient';
import { FilterDropdown, type DropdownOption } from './FilterDropdown';
import { FilterSelect } from './FilterSelect';
import { SearchBox } from './SearchBox';
import { buildWaveOptions } from '@/pages/catalog/ss-editor/utils/waveOptions';

interface ProductFiltersProps {
  filters: {
    productType: SkinSeoulProductType | 'All';
    postStatus: PublicationStatus | 'All';
    stockStatus: StockStatus | 'All';
    wave: string;
    brand: string;
    category: string;
    sku?: string;
    name?: string;
    cpProductStatus: CpProductStatus | 'All';
    cpLastScrapedAt: string;
  };
  brands: BrandTerm[];
  categoryPaths: Array<{
    display: string;
    fullPath: string;
    thumbnailImageUrl?: string;
    level: number;
    isRoot: boolean;
    termId: number;
  }>;
  updateFilters: (filters: any) => void;
  executeSearch: () => void;
  isLoading: boolean;
}

export const ProductFilters: React.FC<ProductFiltersProps> = ({
  filters,
  brands,
  categoryPaths,
  updateFilters,
  executeSearch,
  isLoading,
}) => {
  const brandOptions = useMemo<DropdownOption[]>(
    () =>
      brands
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((brand) => ({
          value: brand.name,
          label: brand.name,
          thumbnailImageUrl: brand.thumbnailImageUrl ?? undefined,
          id: brand.termId,
        })),
    [brands],
  );

  const categoryOptions = useMemo<DropdownOption[]>(
    () =>
      categoryPaths.map((pathObj) => ({
        value: pathObj.fullPath,
        label: pathObj.display,
        thumbnailImageUrl: pathObj.thumbnailImageUrl ?? undefined,
        level: pathObj.level,
        isRoot: pathObj.isRoot,
        id: pathObj.termId,
      })),
    [categoryPaths],
  );

  const productTypeOptions = [
    { value: 'All', label: 'All' },
    { value: SkinSeoulProductType.Simple, label: 'Simple' },
    { value: SkinSeoulProductType.Variable, label: 'Variable' },
  ];

  const postStatusOptions = [
    { value: 'All', label: 'All' },
    { value: PublicationStatus.DRAFT, label: 'Draft' },
    { value: PublicationStatus.PENDING, label: 'Pending' },
    { value: PublicationStatus.PRIVATE, label: 'Private' },
    { value: PublicationStatus.PUBLISH, label: 'Publish' },
  ];

  const stockStatusOptions = [
    { value: 'All', label: 'All' },
    { value: StockStatus.IN_STOCK, label: 'In Stock' },
    { value: StockStatus.OUT_OF_STOCK, label: 'Out of Stock' },
    { value: StockStatus.ON_BACKORDER, label: 'On Backorder' },
  ];

  const waveOptions = useMemo(() => buildWaveOptions(), []);

  const cpProductStatusOptions = [
    { value: 'All', label: 'All' },
    { value: CpProductStatus.IN_STOCK, label: 'In Stock' },
    { value: CpProductStatus.OUT_OF_STOCK, label: 'Out of Stock' },
    { value: CpProductStatus.OBSOLETE, label: 'Obsolete' },
    { value: CpProductStatus.ERROR, label: 'Error' },
  ];

  return (
    <>
      {/* Row 1: Product Type | Post Status | Stock Status | Wave | Category | Brand | Search Button */}
      <Row className="g-2 mb-2">
        <Col md={2}>
          <FilterSelect
            label="Product Type"
            value={filters.productType}
            options={productTypeOptions}
            onChange={(value) => updateFilters({ productType: value as SkinSeoulProductType | 'All' })}
          />
        </Col>
        <Col md={2}>
          <FilterSelect
            label="Post Status"
            value={filters.postStatus}
            options={postStatusOptions}
            onChange={(value) => updateFilters({ postStatus: value as PublicationStatus | 'All' })}
          />
        </Col>
        <Col md={2}>
          <FilterSelect
            label="Stock Status"
            value={filters.stockStatus}
            options={stockStatusOptions}
            onChange={(value) => updateFilters({ stockStatus: value as StockStatus | 'All' })}
          />
        </Col>
        <Col md={1}>
          <FilterSelect
            label="Wave"
            value={filters.wave}
            options={waveOptions}
            onChange={(value) => updateFilters({ wave: value })}
          />
        </Col>
        <Col md={3}>
          <FilterDropdown
            label="Category"
            value={filters.category}
            options={categoryOptions}
            placeholder="All Categories"
            onSelect={(value) => updateFilters({ category: value })}
            showDividers
          />
        </Col>
        <Col md={2}>
          <FilterDropdown
            label="Brand"
            value={filters.brand}
            options={brandOptions}
            placeholder="All Brands"
            onSelect={(value) => updateFilters({ brand: value })}
          />
        </Col>
      </Row>

      {/* Row 2: Search SKU | CP Product Status | CP Last Scraped At | Search Button */}
      <Row className="g-2">
        <Col md={2}>
          <SearchBox
            label="Search SKU"
            value={filters.sku || ''}
            placeholder="Enter SKU..."
            onChange={(value) => updateFilters({ sku: value })}
            onClear={() => updateFilters({ sku: '' })}
            onSearch={executeSearch}
            showClearButton={true}
            showSearchButton={true}
            keepResultsOnClear={true}
          />
        </Col>
        <Col md={3}>
          <SearchBox
            label="Search Name"
            value={filters.name || ''}
            placeholder="Enter Name..."
            onChange={(value) => updateFilters({ name: value })}
            onClear={() => updateFilters({ name: '' })}
            onSearch={executeSearch}
            showClearButton={true}
            showSearchButton={true}
            keepResultsOnClear={true}
          />
        </Col>
        <Col md={2}>
          <FilterSelect
            label="CP Product Status"
            value={filters.cpProductStatus}
            options={cpProductStatusOptions}
            onChange={(value) => updateFilters({ cpProductStatus: value as CpProductStatus | 'All' })}
          />
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.70rem', marginBottom: '0.25rem', fontWeight: '400', color: '#ccc' }}>
              CP Last Scraped At (UTC)
            </Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={filters.cpLastScrapedAt}
              onChange={(e) => updateFilters({ cpLastScrapedAt: e.target.value })}
              style={{ fontSize: '0.85rem' }}
            />
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <Button
            variant="outline-light"
            size="sm"
            onClick={executeSearch}
            disabled={isLoading}
            style={{ width: '100%' }}
          >
            <Search size={14} className="me-1" />
            Search
          </Button>
        </Col>
      </Row>

      {/* Clear All Button */}
      {(filters.productType !== 'All' ||
        filters.postStatus !== 'All' ||
        filters.stockStatus !== 'All' ||
        filters.wave !== 'all' ||
        filters.cpLastScrapedAt ||
        filters.cpProductStatus !== 'All' ||
        filters.brand ||
        filters.category ||
        filters.sku ||
        filters.name) && (
        <div className="d-flex justify-content-end mt-2">
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => {
              updateFilters({
                productType: 'All',
                postStatus: 'All',
                stockStatus: 'All',
                wave: 'all',
                brand: '',
                category: '',
                sku: '',
                name: '',
                cpLastScrapedAt: '',
                cpProductStatus: 'All',
              });
            }}
          >
            <X size={14} className="me-1" />
            Clear All Filters
          </Button>
        </div>
      )}
    </>
  );
};
